// ══════════════════════════════════════════════════════════════════════════════
//  src/limites.js
//  CUANTO PUEDE ESCRIBIR UN ALUMNO, EN UN SOLO SITIO
//
//  ── POR QUE ESTE FICHERO EXISTE ────────────────────────────────────────────
//  El servidor ya rechaza lo que se pasa de largo, y esa es la proteccion de
//  verdad: una pantalla se puede saltar, un endpoint no. Pero rechazar no es
//  suficiente para el alumno.
//
//  Lo que pasaba: pegabas un texto enorme en el chat de Spacy, la caja lo
//  aceptaba sin decir nada —no tenia tope ni contador—, el servidor devolvia un
//  422 por pasarse de largo, y la pantalla lo traducia a "tuve un
//  problema de conexion". Tres cosas mal a la vez: no se podia ver el limite,
//  no se podia notar que se estaba cruzando, y cuando se cruzaba se echaba la
//  culpa a la red. El alumno reintenta, vuelve a fallar, y decide que la app
//  esta rota.
//
//  ── POR QUE LOS NUMEROS VIVEN AQUI Y NO EN CADA PANTALLA ───────────────────
//  Estos numeros tienen que ser LOS MISMOS que los del backend. Repartidos por
//  quince ficheros, el dia que se suba uno se quedan catorce sin subir, y el
//  fallo que sale de eso —el servidor rechaza lo que la pantalla dejaba
//  escribir— es justo el que acabamos de arreglar.
//
//  ⚠️  Si cambias un numero aqui, cambialo tambien en su gemelo del backend.
//      El gemelo esta anotado en cada linea.
// ══════════════════════════════════════════════════════════════════════════════

export const LIMITES = {
    // src/routers/tutor_routes.py · MAX_MENSAJE
    //
    // 500 y no 2.000. Un turno de conversacion son unas 85 palabras; 2.000
    // caracteres no son un mensaje, son un texto pegado — y un texto pegado en
    // mitad de una clase de conversacion no es practicar ingles, es darle de
    // comer al modelo. El tope corto tambien abarata cada turno.
    mensajeSpacy: 500,

    // src/routers/tutor_routes.py · MAX_TEMA
    temaClase: 200,

    // src/consultorio/consultorio_routes.py · MAX_DUDA
    dudaConsultorio: 300,

    // Respuesta libre de un ejercicio. Va a la IA para que la corrija, y
    // ninguna respuesta honesta de un ejercicio pasa de aqui.
    respuestaEjercicio: 1200,

    // Redaccion larga: el unico sitio donde se espera un texto de verdad.
    redaccion: 3000,

    // El contexto que escribe el alumno ("soy chef en un crucero"). Entra en el
    // prompt de sistema, que es el sitio de mas autoridad que hay.
    contexto: 200,

    // src/schemas.py · LearningProfileCreate.profession · max_length=80
    //
    // A que se dedica el alumno («enfermera en urgencias»). Es una ocupacion,
    // no una biografia: 80 caracteres sobran para la mas larga y cortan de raiz
    // que alguien pegue un texto entero en un campo que va DENTRO del prompt de
    // Spacy. Hasta el 23-09-2026 esta caja no tenia gemelo en el servidor: el
    // esquema aceptaba cualquier largo.
    profesion: 80,

    // Mensaje al profesor humano del chat en vivo.
    mensajeChatVivo: 1500,

    // backend-api/src/asistente/asistente_routes.py · MAX_CHAT
    //
    // La pregunta del alumno en el chat de soporte, donde atiende Spacy. 500 y
    // no 1.500: una duda para el asistente son unas 85 palabras, no un texto
    // pegado. El tope corto tambien corta de raiz el pegar codigo o un muro de
    // texto para intentar confundir al modelo. Es la MISMA caja por la que se
    // habla con el humano, asi que este 500 gobierna todo el chat de soporte.
    preguntaChat: 500,

    // Respuesta de minijuego: es una palabra o una frase corta. Un tope
    // generoso aqui sigue siendo diminuto, y evita que se pegue un fichero.
    respuestaMinijuego: 120,

    // ── LO QUE ESCRIBE UN PROFESOR ─────────────────────────────────────────
    // No es un alumno, pero escribe en cajas de texto igual que el, y el
    // servidor las rechaza con el mismo 422 mudo. El tope y el contador van
    // aqui por el mismo motivo que los de arriba.

    // src/routers/portal_profesor.py · EvaluarPayload.nota
    //
    // Lo que los tres numeros no dicen. Mil caracteres son un parrafo largo:
    // suficiente para «le cuesta el pasado simple cuando se pone nervioso» y
    // corto para que siga siendo una nota y no un informe.
    notaEvaluacion: 1000,

    // src/routers/portal_profesor.py · PosponerPayload.motivo
    motivoPosposicion: 300,

    // ── LO QUE SE ESCRIBE AL CREAR LA CUENTA ───────────────────────────────
    //
    // Rob, 11-09-2026, con la pantalla de registro delante: «dice maxLength=120,
    // maxLength=40 y maxLength=254… se ve de mal aspecto como un registro mal
    // hecho».
    //
    // Lo que había pasado es peor que lo que se veía. El atributo estaba escrito
    // FUERA del `<input>`, detrás del `/>`, así que JSX lo pintaba como texto y
    // las cajas se quedaban SIN TOPE. Y los tres números tampoco eran los del
    // servidor: decían 120, 40 y 254 donde el esquema acepta 50, 30 y 100. Aun
    // metiéndolos en su sitio, la caja habría dejado escribir el doble de lo que
    // el servidor acepta, y el alta habría muerto en un 422 mudo.
    //
    // Por eso los números viven aquí y no pegados al input: es el mismo fallo
    // que documenta la cabecera de este fichero, visto otra vez.

    // src/schemas.py · UserCreate.full_name · max_length=50
    nombreCompleto: 50,

    // src/schemas.py · UserCreate.alias · max_length=30
    alias: 30,

    // src/schemas.py · UserCreate.email · max_length=100
    //
    // El estándar (RFC 5321) permite 254. NSL acepta 100 porque es lo que
    // valida el servidor, y el número que manda es el del servidor: un correo
    // de 180 caracteres que la caja deja escribir es un 422 esperando.
    correo: 100,

    // ── LO QUE ESCRIBE GERENCIA EN EL CALENDARIO ──────────────────────────
    // src/routers/calendario_routes.py · EventCreate.notes · max_length=500
    //
    // La caja de notas no tenía tope ni contador: con más de 500 caracteres el
    // servidor devolvía un 422 y la pantalla solo decía que no se pudo guardar.
    notaCalendario: 500,

    // ── LO QUE ESCRIBE GERENCIA EN CHURN ──────────────────────────────────
    // src/routers/admin_churn_routes.py · CancellationCreate.comment
    comentarioBaja: 1000,
    // src/routers/admin_churn_routes.py · RetentionActionPayload.action_detail
    mensajeRetencion: 500,
};

/**
 * ¿Ya conviene avisar de que se acerca el tope?
 *
 * El contador se enseña SIEMPRE —esto no decide si aparece, decide de que
 * COLOR—. Hubo una version en la que el numero solo salia a partir de aqui, y
 * fue un error: con la caja vacia el limite no se veia por ningun lado, asi que
 * la unica forma de enterarse era chocarse con el.
 *
 * Lo que si es util graduar es el enfasis: gris mientras sobra sitio, ambar al
 * acercarse, rojo al llegar. La cifra siempre esta; el grito no.
 */
export function avisaDelTope(largo, tope) {
    return largo >= tope * 0.8;
}

/**
 * Traduce un fallo del servidor a algo que el alumno pueda ACCIONAR.
 *
 * Existe porque el mensaje generico —"problema de conexion"— acusaba a la red
 * de lo que era un texto demasiado largo. Un aviso que señala al culpable
 * equivocado es peor que no avisar: manda al alumno a mirar su wifi.
 */
export function motivoDelFallo(status) {
    if (status === 422 || status === 413) {
        return "Ese mensaje es demasiado largo. Acórtalo un poco y te contesto.";
    }
    if (status === 429) {
        return "Vas muy rápido. Espera unos segundos y seguimos.";
    }
    if (status === 401 || status === 403) {
        return "Tu sesión caducó. Vuelve a entrar y seguimos donde lo dejamos.";
    }
    if (status >= 500) {
        return "Se me cruzaron los cables. Inténtalo otra vez en un momento.";
    }
    return "Tuve un problema de conexión. ¿Lo intentamos de nuevo?";
}

// ═══════════════════════════════════════════════════════════════════════════════
//  LA PRUEBA GRATUITA
//
//  Su duracion estaba escrita a mano en SIETE sitios el 29-08-2026, y tres de
//  ellos son promesas que el alumno lee ANTES de registrarse: la pagina de
//  precios, el correo de verificacion y lo que le contesta Spacy en la
//  portada. Si el producto no cumple lo que dicen, la primera impresion de NSL
//  es un incumplimiento.
//
//  ⚠️  El gemelo es `backend-api/src/prueba_gratis.py` · TRIAL_HORAS.
//      Si cambias el numero aqui, cambialo alli.
// ═══════════════════════════════════════════════════════════════════════════════

// backend-api/src/prueba_gratis.py · TRIAL_HORAS
//   48  = dos dias        (el valor actual)
//   24  = un dia
//   168 = siete dias      (lo que habia antes)
//     6 = unas horas
export const PRUEBA_HORAS = 48;

// Los tokens de IA que trae la prueba. Es el `token_limit` del plan «Freemium»
// en la base, y la pantalla de verificación prometía «100 tokens IA» — un número
// que no era el de ningún plan. Si cambia el plan, cambia aquí.
export const PRUEBA_TOKENS = 350;

/**
 * La duracion en palabras. Ninguna pantalla la escribe a mano.
 */
export function duracionDeLaPrueba(horas = PRUEBA_HORAS) {
    if (horas >= 168 && horas % 168 === 0) {
        const semanas = horas / 168;
        return semanas === 1 ? "1 semana" : `${semanas} semanas`;
    }
    if (horas % 24 === 0) {
        const dias = horas / 24;
        return dias === 1 ? "1 día" : `${dias} días`;
    }
    return horas === 1 ? "1 hora" : `${horas} horas`;
}

// ══════════════════════════════════════════════════════════════════════════════
//  EL TELÉFONO DE WHATSAPP
//
//  Se pide en la pantalla 5.5 del diagnóstico. México y sólo México: NSL vende
//  en México y el mensaje lo escribe gerencia desde su propio WhatsApp, así que
//  no hay ningún caso en el que un número de otro país sirva para algo.
//
//  ⚠️  Gemelo en el servidor: src/routers/onboarding_routes.py · _safe_phone,
//      que exige exactamente +52 y diez dígitos. Si cambia uno, cambia el otro.
// ══════════════════════════════════════════════════════════════════════════════

export const TELEFONO_MX = {
    lada: '+52',
    digitos: 10,
};

/** Se queda solo con las cifras: pegar «(55) 1234-5678» tiene que funcionar. */
export function soloDigitos(texto) {
    return String(texto || '').replace(/\D/g, '').slice(0, TELEFONO_MX.digitos);
}

/**
 * Como se escribe un número mexicano de verdad. Las cuatro ciudades grandes
 * usan lada de dos cifras (55 1234 5678) y el resto del país de tres
 * (998 123 4567); agruparlo mal hace que el alumno dude de si lo escribió bien.
 */
export function formateaTelefonoMX(digitos) {
    const d = soloDigitos(digitos);
    const ladaCorta = ['55', '56', '33', '81'].includes(d.slice(0, 2));
    const cortes = ladaCorta ? [2, 6] : [3, 6];
    const partes = [d.slice(0, cortes[0]), d.slice(cortes[0], cortes[1]), d.slice(cortes[1])];
    return partes.filter(Boolean).join(' ');
}

/** Lo que se guarda y lo que viaja al servidor: E.164, sin espacios. */
export function telefonoCompleto(digitos) {
    const d = soloDigitos(digitos);
    return d ? `${TELEFONO_MX.lada}${d}` : null;
}

export function telefonoValido(digitos) {
    return soloDigitos(digitos).length === TELEFONO_MX.digitos;
}
